<template>
  <q-drawer
    v-model="isVisible"
    side="right"
    :width="$q.screen.lt.md ? Math.min(420, $q.screen.width * 0.92) : 440"
    :breakpoint="0"
    :behavior="'mobile'"
    bordered
    class="notification-drawer">
    <!-- The drawer is a modal surface, but Quasar gives it no Escape handling of
         its own; the panel takes the focus when it opens (see the isVisible
         watcher) so the key lands here instead of on the page behind it. -->
    <div
      ref="panel"
      class="notification-center column no-wrap"
      tabindex="-1"
      @keydown.esc="close">
      <header class="notification-center__head">
        <div class="notification-center__head-text">
          <div class="notification-center__title-row">
            <q-icon
              :name="symRoundedNotifications"
              class="notification-center__title-icon" />
            <span class="notification-center__title">{{ t('title') }}</span>
            <span
              :class="['notification-center__realtime', realTimeConnected ? 'is-on' : 'is-off']">
              <span class="notification-center__realtime-dot"></span>
              <q-tooltip :delay="350">
                {{ realTimeConnected ? t('realtime.connected') : t('realtime.disconnected') }}
              </q-tooltip>
            </span>
          </div>
          <div class="notification-center__subtitle">
            {{ subtitleText }}
          </div>
        </div>
        <div class="notification-center__head-actions">
          <button
            type="button"
            class="notification-center__icon-btn app-focus-ring"
            :disabled="loading"
            :aria-label="t('actions.refresh')"
            @click="refreshNotifications">
            <q-icon
              :class="{ 'is-spinning': loading }"
              :name="symRoundedRefresh" />
            <q-tooltip :delay="350">{{ t('actions.refresh') }}</q-tooltip>
          </button>
          <button
            type="button"
            class="notification-center__icon-btn app-focus-ring"
            :disabled="unreadCount === 0"
            :aria-label="t('actions.markAllRead')"
            @click="confirmMarkAllRead">
            <q-icon :name="symRoundedDoneAll" />
            <q-tooltip :delay="350">{{ t('actions.markAllRead') }}</q-tooltip>
          </button>
          <button
            type="button"
            :class="['notification-center__icon-btn', 'app-focus-ring', { 'is-active': hasActiveFilters }]"
            :aria-label="t('filters.priority')">
            <q-icon :name="symRoundedFilterList" />
            <q-tooltip :delay="350">{{ t('filters.priority') }}</q-tooltip>
            <q-menu>
              <q-list dense style="min-width: 200px">
                <q-item-label header>{{ t('filters.priority') }}</q-item-label>
                <q-item
                  v-for="priority in priorities"
                  :key="priority"
                  clickable
                  @click="togglePriorityFilter(priority)">
                  <q-item-section avatar>
                    <q-checkbox
                      :model-value="store.filters.priority === priority"
                      @update:model-value="togglePriorityFilter(priority)" />
                  </q-item-section>
                  <q-item-section>{{ t(`priority.${priority}`) }}</q-item-section>
                </q-item>
                <q-separator />
                <q-item
                  clickable
                  @click="clearFilters">
                  <q-item-section avatar>
                    <q-icon :name="symRoundedClearAll" />
                  </q-item-section>
                  <q-item-section>{{ t('filters.clear') }}</q-item-section>
                </q-item>
              </q-list>
            </q-menu>
          </button>
          <button
            type="button"
            class="notification-center__icon-btn app-focus-ring"
            :aria-label="t('actions.collapse')"
            @click="close">
            <q-icon :name="symRoundedClose" />
          </button>
        </div>
      </header>

      <!-- A real tablist, not four buttons: one tab stop for the whole bar and the
           arrows walk it (see onTabKeydown), which is what an AT user expects here. -->
      <div
        ref="tablist"
        class="notification-center__tabs"
        role="tablist"
        :aria-label="t('tabs.listLabel')">
        <button
          v-for="(tab, index) in tabs"
          :id="`${tabsId}-${tab.value}`"
          :key="tab.value"
          type="button"
          role="tab"
          data-roving-item
          :aria-selected="currentTab === tab.value"
          :aria-controls="`${tabsId}-panel`"
          :aria-label="tab.ariaLabel"
          :tabindex="currentTab === tab.value ? 0 : -1"
          :class="['notification-tab', 'app-focus-ring', { 'is-active': currentTab === tab.value }]"
          @click="currentTab = tab.value"
          @keydown="onTabKeydown($event, index)">
          <span>{{ tab.label }}</span>
          <span
            v-if="tab.count > 0"
            :class="['notification-tab__count', { 'is-active': currentTab === tab.value }]">
            {{ tab.count }}
          </span>
        </button>
      </div>

      <!-- Focusable so an empty tab is still reachable by keyboard; the shared ring
           makes that stop visible instead of silently swallowing the caret. -->
      <div
        :id="`${tabsId}-panel`"
        class="notification-center__body app-focus-ring"
        role="tabpanel"
        :aria-labelledby="`${tabsId}-${currentTab}`"
        tabindex="0">
        <NotificationList
          :notifications="currentList"
          :loading="loading"
          :has-more="currentTab === 'all' && hasMore"
          @notification-click="handleNotificationClick"
          @mark-read="markNotificationRead"
          @mark-unread="markNotificationUnread"
          @delete="confirmDeleteNotification"
          @execute-action="executeNotificationAction"
          @retry="retryProgressiveNotification"
          @cancel="cancelProgressiveNotification"
          @load-more="loadMore" />
      </div>

      <div
        v-if="error"
        class="notification-center__error">
        <q-icon
          :name="symRoundedError"
          class="notification-center__error-icon" />
        <div class="notification-center__error-text">
          <div class="notification-center__error-title">{{ t('messages.loadFailed') }}</div>
          <div class="notification-center__error-msg">{{ error }}</div>
        </div>
        <q-btn
          unelevated
          dense
          no-caps
          color="primary"
          :label="t('actions.retry')"
          @click="refreshNotifications" />
      </div>
    </div>
  </q-drawer>
</template>

<script setup lang="ts">
import {
  symRoundedClearAll,
  symRoundedClose,
  symRoundedDoneAll,
  symRoundedError,
  symRoundedFilterList,
  symRoundedNotifications,
  symRoundedRefresh,
} from '@quasar/extras/material-symbols-rounded'
import { Dialog } from 'quasar'
import { showErrorNotification, showSuccessNotification } from 'src/CrudManager/Core/Utils/ToastNotification'
import { useCompositeI18n } from 'src/CrudManager/Core/composables/useCompositeI18n'
import { useRovingFocus } from 'src/CrudManager/Core/composables/useRovingFocus'
import { computed, inject, nextTick, provide, ref, useId, useTemplateRef, watch } from 'vue'
import { useRouter } from 'vue-router'
import NotificationList from './components/NotificationList.vue'
import { useNotificationCenter } from './composables/useNotificationCenter'
import { useRendererRegistry } from './composables/useRendererRegistry'
import type { ActionExecutionResult, Notification, NotificationAction, NotificationPriority } from './types'

interface Props {
  modelValue: boolean
  userId?: string
  autoLoad?: boolean
  enableRealtime?: boolean
}

interface Emits {
  (e: 'update:model-value', value: boolean): void
  (e: 'notification-clicked', notification: Notification): void
  (e: 'action-executed', notification: Notification, actionName: string, result: ActionExecutionResult): void
}

const props = withDefaults(defineProps<Props>(), {
  userId: undefined,
  autoLoad: true,
  enableRealtime: true,
})

const emit = defineEmits<Emits>()
const { t } = useCompositeI18n('core.NotificationCenter')
const router = useRouter()
const { initialize: initializeRendererRegistry } = useRendererRegistry()

const {
  store,
  currentTab,
  refreshNotifications,
  loadMore,
  markNotificationRead,
  markNotificationUnread,
  markAllRead,
  deleteNotification,
  retryProgressiveNotification,
  cancelProgressiveNotification,
  handleNotificationClick,
  setTab,
  actionRegistry,
  notifications,
  unreadCount,
  loading,
  error,
  hasMore,
  realTime,
} = useNotificationCenter(props.userId, props.autoLoad, props.enableRealtime)

const isVisible = computed({
  get: () => props.modelValue,
  set: (value) => emit('update:model-value', value),
})

const panel = ref<HTMLElement | null>(null)
// the control that opened the panel, so the caret can go back where it came from
let opener: HTMLElement | null = null

const unreadNotifications = computed(() => store.unreadNotifications)
const progressiveNotifications = computed(() => store.progressiveNotifications)
const mandatoryNotifications = computed(() => store.mandatoryNotifications)
const realTimeConnected = computed(() => realTime.isConnected)

const filteredNotifications = computed(() => {
  let filtered = [...notifications.value]
  if (store.filters.priority) {
    filtered = filtered.filter((n) => n.data.priority === store.filters.priority)
  }
  return filtered
})

const priorities: NotificationPriority[] = ['low', 'normal', 'high', 'urgent']

const hasActiveFilters = computed(() => store.filters.priority !== null)

// One id root per instance, so the tab/tabpanel wiring stays unique even if a
// second panel is ever mounted in the same document.
const tabsId = useId()
const tablist = useTemplateRef<HTMLElement>('tablist')

/**
 * The count pill lives inside the tab, so its number would reach a screen reader
 * as a bare "Toutes 12" with nothing saying what was counted.
 */
const tabAriaLabel = (label: string, count: number) => {
  if (count === 0) return t('tabs.countNone', { label })
  if (count === 1) return t('tabs.countOne', { label })
  return t('tabs.countMany', { label, count })
}

const tabs = computed(() =>
  [
    { value: 'all' as const, count: filteredNotifications.value.length },
    { value: 'unread' as const, count: unreadCount.value },
    { value: 'progressive' as const, count: progressiveNotifications.value.length },
    { value: 'mandatory' as const, count: mandatoryNotifications.value.length },
  ].map((tab) => {
    const label = t(`tabs.${tab.value}`)
    return { ...tab, label, ariaLabel: tabAriaLabel(label, tab.count) }
  })
)

const { onKeydown: onTabKeydown } = useRovingFocus({
  values: () => tabs.value.map((tab) => tab.value),
  select: (value) => {
    currentTab.value = value
  },
  container: tablist,
})

const currentList = computed(() => {
  switch (currentTab.value) {
    case 'unread':
      return unreadNotifications.value
    case 'progressive':
      return progressiveNotifications.value
    case 'mandatory':
      return mandatoryNotifications.value
    default:
      return filteredNotifications.value
  }
})

const subtitleText = computed(() => {
  const c = unreadCount.value
  if (c === 0) return t('subtitle.none')
  if (c === 1) return t('subtitle.one')
  return t('subtitle.many', { count: c })
})

// Event Bus for component communication
const eventBus = inject('eventBus', null)
provide('eventBus', eventBus)

const close = () => {
  isVisible.value = false
}

const confirmMarkAllRead = () => {
  if (unreadCount.value === 0) return
  Dialog.create({
    title: t('confirmation.markAllRead'),
    cancel: true,
    persistent: true,
  }).onOk(() => {
    markAllRead()
  })
}

const confirmDeleteNotification = (notification: Notification) => {
  Dialog.create({
    title: t('confirmation.delete'),
    message: notification.data.title,
    cancel: true,
    persistent: true,
  }).onOk(() => {
    deleteNotification(notification)
  })
}

const executeNotificationAction = async (notification: Notification, actionName: string, action: NotificationAction) => {
  let confirmed = true

  if (action.confirmable) {
    const result = await new Promise<boolean>((resolve) => {
      Dialog.create({
        title: t('confirmation.executeAction'),
        message: `${action.label}: ${notification.data.title}`,
        cancel: true,
        persistent: true,
      })
        .onOk(() => resolve(true))
        .onCancel(() => resolve(false))
    })
    confirmed = result
  }

  if (!confirmed) return

  try {
    const actionData = action.data || {}
    const result = await actionRegistry.executeAction(actionName, notification, actionData)

    if (result.success) {
      showSuccessNotification(result.message || 'Action executed successfully')
      if (!notification.read_at) {
        markNotificationRead(notification)
      }
      if (result.redirectUrl) {
        await router.push(result.redirectUrl)
      }
    } else {
      showErrorNotification(result.error || 'Action execution failed')
    }

    emit('action-executed', notification, actionName, {
      success: result.success,
      message: result.message,
      error: result.error,
      redirect_url: result.redirectUrl,
      data: result.data,
    })
  } catch (err) {
    const errorMessage = err instanceof Error ? err.message : 'Action execution failed'
    console.error(`Action '${actionName}' failed:`, err)
    showErrorNotification(errorMessage)

    emit('action-executed', notification, actionName, {
      success: false,
      error: errorMessage,
    })
  }
}

const togglePriorityFilter = (priority: NotificationPriority) => {
  const currentPriority = store.filters.priority
  store.setFilters({
    priority: currentPriority === priority ? null : priority,
  })
}

const clearFilters = () => {
  store.clearFilters()
}

watch(currentTab, (newTab) => {
  setTab(newTab)
})

initializeRendererRegistry()

/**
 * Opening the panel used to leave the caret on the header bell: Tab kept walking
 * the page underneath and Escape reached nobody. The panel holds the focus while
 * it is open and hands it back to whatever opened it on close.
 */
watch(isVisible, async (visible) => {
  if (!visible) {
    // A control that was re-rendered away while the panel was open cannot take the
    // caret back; leaving it on the hidden panel is worse than dropping it to the body.
    if (opener?.isConnected) opener.focus()
    opener = null
    return
  }

  if (props.autoLoad) {
    if (mandatoryNotifications.value.length > 0) {
      currentTab.value = 'mandatory'
    } else if (unreadCount.value > 0) {
      currentTab.value = 'unread'
    }
  }

  opener = document.activeElement instanceof HTMLElement ? document.activeElement : null
  await nextTick()
  // Closed while we waited: the close branch has already handed the caret back, so
  // taking it now would park it on a hidden panel.
  if (isVisible.value) panel.value?.focus()
})
</script>

<style lang="scss" scoped>
.notification-drawer {
  background: var(--q-dark);
}

.notification-center {
  height: 100vh;
  width: 100%;
  background: var(--q-dark);
  color: #ffffff;
  // it only holds the focus to catch Escape; the ring belongs on the controls
  outline: none;
}

.body--light .notification-center,
.body--light .notification-drawer {
  background: #ffffff;
  color: #0f1419;
}

.notification-center__head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  padding: 16px 16px 8px;
}

.notification-center__head-text {
  min-width: 0;
}

.notification-center__title-row {
  display: flex;
  align-items: center;
  gap: 8px;
}

.notification-center__title-icon {
  font-size: 1.2rem;
  opacity: 0.85;
}

.notification-center__title {
  font-size: 1.05rem;
  font-weight: 700;
  letter-spacing: 0.01em;
}

.notification-center__realtime {
  display: inline-flex;
  align-items: center;
  margin-left: 2px;
}

.notification-center__realtime-dot {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: $grey-6;
  display: inline-block;
}

.notification-center__realtime.is-on .notification-center__realtime-dot {
  background: var(--q-positive);
  box-shadow: 0 0 0 0 color-mix(in srgb, var(--q-positive) 60%, transparent);
  animation: realtime-pulse 2.4s cubic-bezier(0.4, 0, 0.2, 1) infinite;
}

@keyframes realtime-pulse {
  0% {
    box-shadow: 0 0 0 0 color-mix(in srgb, var(--q-positive) 55%, transparent);
  }
  70% {
    box-shadow: 0 0 0 6px color-mix(in srgb, var(--q-positive) 0%, transparent);
  }
  100% {
    box-shadow: 0 0 0 0 color-mix(in srgb, var(--q-positive) 0%, transparent);
  }
}

.notification-center__subtitle {
  margin-top: 2px;
  font-size: 0.74rem;
  color: $grey-6;
}

.notification-center__head-actions {
  display: inline-flex;
  align-items: center;
  gap: 2px;
  flex-shrink: 0;
}

.notification-center__icon-btn {
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 30px;
  border: none;
  border-radius: 8px;
  background: transparent;
  color: inherit;
  opacity: 0.65;
  cursor: pointer;
  transition:
    opacity 0.15s cubic-bezier(0.4, 0, 0.2, 1),
    background-color 0.15s cubic-bezier(0.4, 0, 0.2, 1);

  &:hover:not(:disabled) {
    opacity: 1;
    background: rgba(255, 255, 255, 0.08);
  }

  &:disabled {
    opacity: 0.25;
    cursor: not-allowed;
  }

  &.is-active {
    color: var(--q-primary);
    opacity: 1;
  }

  .q-icon {
    font-size: 1rem;
  }
}

.body--light .notification-center__icon-btn:hover:not(:disabled) {
  background: rgba(0, 0, 0, 0.06);
}

.is-spinning {
  animation: notif-spin 0.85s linear infinite;
}

@keyframes notif-spin {
  to {
    transform: rotate(360deg);
  }
}

.notification-center__tabs {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  padding: 4px 16px 10px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.06);
}

.body--light .notification-center__tabs {
  border-bottom-color: rgba(0, 0, 0, 0.06);
}

.notification-tab {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 10px;
  border-radius: 999px;
  border: 1px solid rgba(255, 255, 255, 0.12);
  background: transparent;
  color: rgba(255, 255, 255, 0.7);
  font-size: 0.72rem;
  font-weight: 500;
  cursor: pointer;
  transition:
    background-color 0.15s cubic-bezier(0.4, 0, 0.2, 1),
    border-color 0.15s cubic-bezier(0.4, 0, 0.2, 1),
    color 0.15s cubic-bezier(0.4, 0, 0.2, 1);

  &:hover {
    background: rgba(255, 255, 255, 0.05);
    color: #fff;
  }

  &.is-active {
    background: color-mix(in srgb, var(--q-primary) 20%, transparent);
    border-color: color-mix(in srgb, var(--q-primary) 55%, transparent);
    color: var(--q-primary);
  }
}

.body--light .notification-tab {
  border-color: rgba(0, 0, 0, 0.12);
  color: rgba(0, 0, 0, 0.7);

  &:hover {
    background: rgba(0, 0, 0, 0.04);
    color: #000;
  }
}

.notification-tab__count {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 18px;
  height: 18px;
  padding: 0 5px;
  font-size: 0.62rem;
  font-weight: 700;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.1);
  color: inherit;

  &.is-active {
    background: color-mix(in srgb, var(--q-primary) 35%, transparent);
    color: var(--q-primary);
  }
}

.body--light .notification-tab__count {
  background: rgba(0, 0, 0, 0.07);
}

.notification-center__body {
  flex: 1 1 auto;
  min-height: 0;
  overflow: hidden;
}

.notification-center__error {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
  border-top: 1px solid color-mix(in srgb, var(--q-negative) 25%, transparent);
  background: color-mix(in srgb, var(--q-negative) 8%, transparent);
}

.notification-center__error-icon {
  color: var(--q-negative);
  font-size: 1.4rem;
  flex-shrink: 0;
}

.notification-center__error-text {
  flex: 1 1 auto;
  min-width: 0;
}

.notification-center__error-title {
  font-size: 0.82rem;
  font-weight: 600;
}

.notification-center__error-msg {
  font-size: 0.72rem;
  color: $grey-6;
}
</style>
